import * as React from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { Skeleton } from "./skeleton";
import { Button } from "./button";

const SKELETON_ROWS = 6;

/**
 * DataTable — reusable table replacing antd Table.
 *
 * columns: [{
 *   title, dataIndex, key?, width?, align?, render?(val,row,idx), sorter?(a,b),
 *   defaultSortOrder?, fixed?, ellipsis?, filters?, onFilter?
 * }]
 * data: row[]
 * rowKey: string | (row) => string
 * loading: boolean
 * bordered: boolean
 * size: 'middle' | 'small'
 * scroll: { x: number }
 * pagination: {current, pageSize, showSizeChanger?, pageSizeOptions?, showTotal?} | false
 * onPaginationChange: (page, pageSize) => void
 * rowClassName: (row, idx) => string
 * summary: (visibleData) => React.ReactNode  (renders a <tfoot> row)
 */
const DataTable = ({
  columns = [],
  data = [],
  rowKey = "key",
  loading = false,
  bordered = false,
  size = "middle",
  scroll,
  pagination,
  onPaginationChange,
  rowClassName,
  summary,
}) => {
  const [sortKey, setSortKey] = React.useState(() => {
    const col = columns.find((c) => c.defaultSortOrder);
    return col ? (col.dataIndex || col.key) : null;
  });
  const [sortDir, setSortDir] = React.useState(() => {
    const col = columns.find((c) => c.defaultSortOrder);
    return col?.defaultSortOrder ?? "asc";
  });

  const getRowKey = (row, idx) => {
    if (typeof rowKey === "function") return rowKey(row);
    return row[rowKey] ?? idx;
  };

  const handleSort = (col) => {
    if (!col.sorter) return;
    const key = col.dataIndex || col.key;
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedData = React.useMemo(() => {
    if (!sortKey) return data;
    const col = columns.find((c) => (c.dataIndex || c.key) === sortKey);
    if (!col?.sorter) return data;
    const sorted = [...data].sort((a, b) => col.sorter(a, b));
    return sortDir === "desc" ? sorted.reverse() : sorted;
  }, [data, columns, sortKey, sortDir]);

  const { current = 1, pageSize = 20 } = pagination || {};
  const total = sortedData.length;

  const pagedData = pagination === false
    ? sortedData
    : sortedData.slice((current - 1) * pageSize, current * pageSize);

  const totalPages = pagination === false ? 1 : Math.max(1, Math.ceil(total / pageSize));

  const handlePageChange = (page) => {
    onPaginationChange?.(page, pageSize);
  };

  const handlePageSizeChange = (e) => {
    onPaginationChange?.(1, Number(e.target.value));
  };

  const pageSizeOptions = pagination?.pageSizeOptions ?? [10, 20, 50, 100];
  const showSizeChanger = pagination?.showSizeChanger ?? false;

  const showTotal = pagination?.showTotal;
  const start = (current - 1) * pageSize + 1;
  const end = Math.min(current * pageSize, total);

  const cellPad = size === "small" ? "px-2 py-1" : "px-3 py-2";
  const headPad = size === "small" ? "px-2 py-1.5" : "px-3 py-2.5";

  const minWidth = scroll?.x ? `${scroll.x}px` : undefined;

  return (
    <div className="flex flex-col gap-3">
      <div
        className={cn(
          "w-full overflow-x-auto rounded-lg",
          bordered ? "border border-gray-200" : ""
        )}
      >
        <table
          className="w-full border-collapse text-sm"
          style={minWidth ? { minWidth } : undefined}
        >
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {columns.map((col, ci) => {
                const key = col.dataIndex || col.key || ci;
                const isSorted = sortKey === key;
                const fixedRight = col.fixed === "right";
                return (
                  <th
                    key={key}
                    style={{ width: col.width, minWidth: col.width }}
                    className={cn(
                      headPad,
                      "text-left font-semibold text-xs uppercase tracking-wide text-gray-500 whitespace-nowrap select-none",
                      col.align === "right" && "text-right",
                      col.align === "center" && "text-center",
                      col.sorter && "cursor-pointer hover:text-gray-700",
                      fixedRight && "sticky right-0 bg-gray-50 shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.05)]",
                      bordered && ci > 0 && "border-l border-gray-200"
                    )}
                    onClick={() => handleSort(col)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.title}
                      {col.sorter && (
                        <span className="text-gray-400">
                          {isSorted ? (
                            sortDir === "asc" ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronsUpDown className="h-3.5 w-3.5" />
                          )}
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: SKELETON_ROWS }).map((_, ri) => (
                  <tr key={ri} className="border-b border-gray-100">
                    {columns.map((col, ci) => (
                      <td
                        key={ci}
                        className={cn(cellPad, bordered && ci > 0 && "border-l border-gray-200")}
                      >
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              : pagedData.length === 0
              ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="py-12 text-center text-gray-400 text-sm"
                    >
                      Sin registros
                    </td>
                  </tr>
                )
              : pagedData.map((row, ri) => {
                  const extraClass = rowClassName ? rowClassName(row, ri) : "";
                  return (
                    <tr
                      key={getRowKey(row, ri)}
                      className={cn(
                        "border-b border-gray-100 hover:bg-gray-50/60 transition-colors",
                        extraClass
                      )}
                    >
                      {columns.map((col, ci) => {
                        const fixedRight = col.fixed === "right";
                        const rawVal = col.dataIndex ? row[col.dataIndex] : undefined;
                        const rendered = col.render
                          ? col.render(rawVal, row, ri)
                          : rawVal;
                        return (
                          <td
                            key={col.dataIndex || col.key || ci}
                            style={{ width: col.width, minWidth: col.width }}
                            className={cn(
                              cellPad,
                              "text-gray-700",
                              col.align === "right" && "text-right",
                              col.align === "center" && "text-center",
                              col.ellipsis && "max-w-0 truncate",
                              fixedRight &&
                                "sticky right-0 bg-white shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.05)]",
                              bordered && ci > 0 && "border-l border-gray-200"
                            )}
                          >
                            {rendered ?? "—"}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
          </tbody>
          {summary && !loading && pagedData.length > 0 && (
            <tfoot>
              {summary(pagedData)}
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      {pagination !== false && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            {showTotal && (
              <span>{showTotal(total, [start, end])}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {showSizeChanger && (
              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {pageSizeOptions.map((s) => (
                  <option key={s} value={s}>{s} / pág.</option>
                ))}
              </select>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={current <= 1}
              onClick={() => handlePageChange(current - 1)}
            >
              Anterior
            </Button>
            <span className="px-2 text-sm font-medium">
              {current} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={current >= totalPages}
              onClick={() => handlePageChange(current + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export { DataTable };
